import { useState, type ReactNode } from 'react';
import { Archive, History, Pencil, Save, Upload } from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { BreadcrumbBar, type Crumb } from './BreadcrumbBar';
import { VersionHistoryModal, type EditorHistorySource } from './VersionHistoryModal';
import type { EditorMode } from './DocContentEditor';
import { cn } from '@/lib/utils';

export interface FullEditorTab {
  key: string;
  label: string;
}

interface FullEditorShellProps {
  /** "← Docs | {source} | {document}" — targets carry the query string. */
  crumbs: Crumb[];
  title: string;
  /** Status badge(s) next to the title. */
  titleBadges?: ReactNode;
  /** Preview-first (R2): the shell renders Edit; Save returns to preview. */
  mode: EditorMode;
  onModeChange: (mode: EditorMode) => void;
  /** Hide the Edit toggle entirely (read-only surfaces). */
  canEdit?: boolean;
  dirty?: boolean;
  saving?: boolean;
  /** Manual flush — autosave still runs while editing (R3). The Save button
   *  calls this, then returns to preview: it doubles as "done editing". */
  onSave?: () => void;
  /** Publish to the knowledge base; the caller owns its dialog/flow. */
  onPublish?: () => void;
  publishLabel?: string;
  publishDisabled?: boolean;
  /** View History (R4) — omit while a document type has no checkpoint source. */
  history?: EditorHistorySource;
  onArchive?: () => void;
  showArchive?: boolean;
  /** Type-specific actions, rendered before the shared ones. */
  extraActions?: ReactNode;
  /** Rendered between the header and the tab strip (e.g. traceability links). */
  belowHeader?: ReactNode;
  tabs?: FullEditorTab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  children: ReactNode;
}

/**
 * The consolidated full editor chrome every document type composes
 * (specs/changes/consolidated-full-editor R1): ONE bar carrying the
 * breadcrumbs back to the source page, the document title + status badges,
 * and the actions (Edit, Save, Publish, History, Archive) — plus the optional
 * tab strip and the version-history modal. There is no Preview button: the
 * editor opens in preview, and Save both flushes and returns there.
 */
export function FullEditorShell({
  crumbs,
  title,
  titleBadges,
  mode,
  onModeChange,
  canEdit = true,
  dirty,
  saving,
  onSave,
  onPublish,
  publishLabel = 'Publish',
  publishDisabled,
  history,
  onArchive,
  showArchive = true,
  extraActions,
  belowHeader,
  tabs,
  activeTab,
  onTabChange,
  children,
}: FullEditorShellProps) {
  const [showHistory, setShowHistory] = useState(false);

  return (
    <div className="flex flex-col h-full">
      {/* One bar: breadcrumbs · title + badges · actions. */}
      <BreadcrumbBar
        crumbs={crumbs}
        current={
          <span className="flex items-center gap-2 min-w-0">
            <h1 className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{title}</h1>
            {titleBadges}
          </span>
        }
        actions={
          <>
            {(saving || dirty) && (
              <span className={cn('flex items-center gap-1 text-[11px]', saving ? 'text-slate-400' : 'text-amber-500')}>
                {saving && <Spinner size="sm" />}
                {saving ? 'Saving…' : 'Unsaved changes'}
              </span>
            )}
            {extraActions}
            {canEdit && mode === 'preview' && (
              <button
                onClick={() => onModeChange('edit')}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
                title="Edit in the WYSIWYG editor"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            )}
            {mode === 'edit' && (
              <button
                onClick={() => {
                  onSave?.();
                  onModeChange('preview');
                }}
                disabled={saving}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                title="Save and return to the document"
              >
                <Save className="w-3.5 h-3.5" /> Save
              </button>
            )}
            {onPublish && (
              <button
                onClick={onPublish}
                disabled={publishDisabled}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-300 dark:border-slate-700 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5" /> {publishLabel}
              </button>
            )}
            {history && (
              <button
                onClick={() => setShowHistory(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                title="View version history"
              >
                <History className="w-3.5 h-3.5" /> History
              </button>
            )}
            {onArchive && showArchive && (
              <button
                onClick={onArchive}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Archive className="w-3.5 h-3.5" /> Archive
              </button>
            )}
          </>
        }
      />

      {belowHeader}

      {/* Tabs */}
      {tabs && tabs.length > 0 && (
        <div className="flex gap-0 px-6 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shrink-0 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => onTabChange?.(tab.key)}
              className={cn(
                'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>

      {showHistory && history && (
        <VersionHistoryModal title={title} source={history} onClose={() => setShowHistory(false)} />
      )}
    </div>
  );
}
